'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Finance, Interfaces } from 'doodle-icons';
import RoughAnnotation from '@/components/common/RoughAnnotation';
import StationeryCapabilityBoard, { type StationeryCapabilityPanel } from '@/components/common/StationeryCapabilityBoard';
import { trackEvent } from '@/lib/analytics';

/* ── Scroll reveal helper ────────────────────────── */
function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ── Problem sticky notes ────────────────────────── */
const PROBLEMS = [
  { cat: 'Volume', title: '30\u201350 papers', body: 'Per student, per week. Manual checking doesn\u2019t scale.', rotation: '-0.6deg' },
  { cat: 'Time', title: '10\u201320 hrs/week', body: 'Lost to checking \u2014 per teacher. That\u2019s teaching time, gone.', rotation: '0.5deg' },
  { cat: 'Feedback', title: 'Late & inconsistent', body: 'Feedback arrives days later. Quality varies by teacher.', rotation: '-0.4deg' },
  { cat: 'Insight', title: 'Zero concept data', body: 'Marks tell you who passed. Not why anyone struggled.', rotation: '0.7deg' },
];

const FEATURE_PANELS: StationeryCapabilityPanel[] = [
  {
    eyebrow: 'Track',
    title: 'JEE and NEET mastery, node by node.',
    description: 'The centre gets one structured view of which concepts are stable, fragile, or repeatedly missed.',
    icon: <Finance.TrendUp width={22} height={22} fill="currentColor" />,
    accent: 'blue',
    rotation: '-0.8deg',
    points: [
      {
        label: 'JEE/NEET Concept Mastery',
        detail: 'See exactly where each student is stuck, down to the formula resolution.',
        icon: <Finance.TrendUp width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Intervention Cohorts',
        detail: 'Group students by shared concept failures and assign focused remediation fast.',
        icon: <Interfaces.User width={18} height={18} fill="currentColor" />,
      },
    ],
  },
  {
    eyebrow: 'Explain',
    title: 'Give parents and tutors the same source of truth.',
    description: 'Instead of disconnected marks, centres get shareable evidence of progress and weak concepts.',
    icon: <Interfaces.Doc width={22} height={22} fill="currentColor" />,
    accent: 'green',
    rotation: '0.7deg',
    points: [
      {
        label: 'Parent Intelligence Reports',
        detail: 'Show mastery patterns and concept movement instead of just test scores.',
        icon: <Interfaces.Doc width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Centre-wide Visibility',
        detail: 'Tutors, branches, and batches align around one shared academic picture.',
        icon: <Interfaces.Analytics width={18} height={18} fill="currentColor" />,
      },
    ],
  },
  {
    eyebrow: 'Scale',
    title: 'Move tutor time from checking to intervention.',
    description: 'The operating leverage comes from grading faster and spending the recovered time where it matters.',
    icon: <Interfaces.Zap width={22} height={22} fill="currentColor" />,
    accent: 'amber',
    rotation: '-0.3deg',
    points: [
      {
        label: '3x Tutor Capacity',
        detail: 'Handle more students per tutor without losing feedback quality.',
        icon: <Interfaces.Zap width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Consistent Quality',
        detail: 'Every batch gets the same standard of checking and diagnostic detail.',
        icon: <Interfaces.Shield width={18} height={18} fill="currentColor" />,
      },
    ],
  },
];

/* ── Comparison rows ─────────────────────────────── */
const COMP_ROWS = [
  { label: 'Output per paper', manual: 'A score', intel: 'Curriculum graph update' },
  { label: 'Concept tracking', manual: 'None', intel: 'Formula-level' },
  { label: 'Teacher time', manual: '10\u201320 hrs/week', intel: 'Minutes' },
  { label: 'After 6 months', manual: 'Nothing', intel: 'Structured misconception dataset' },
  { label: 'Parent reports', manual: 'Manual', intel: 'Auto-generated, concept-mapped' },
];

/* ── FAQ ─────────────────────────────────────────── */
const FAQS = [
  {
    q: 'Does IntelGrader support JEE and NEET syllabi?',
    a: 'IntelGrader works with any teacher-defined curriculum. Upload your JEE/NEET worksheets \u2014 we map them into concept nodes automatically.',
  },
  {
    q: 'How many papers can it handle per week?',
    a: 'There\u2019s no limit. Each paper takes under 30 seconds. A centre with 100 students can process all weekly submissions in a fraction of the time.',
  },
  {
    q: 'What data do teachers get that they don\u2019t have today?',
    a: 'A live curriculum graph: which student is stuck on which concept, which formula they misapplied, and how mastery changes over time. Manual grading produces none of this.',
  },
  {
    q: 'Is it safe for student data?',
    a: 'All data is encrypted in transit and at rest. We follow GDPR-aligned data practices. See our security page for details.',
  },
];

/* ── Page component ──────────────────────────────── */
const CoachingCentersPage: React.FC = () => {
  const hero = useReveal(0.1);
  const problem = useReveal();
  const features = useReveal();
  const comparison = useReveal();
  const faq = useReveal();

  return (
    <div className="flex flex-col">
      {/* ——— HERO ——— */}
      <section
        ref={hero.ref}
        className="page-hero page-hero--coaching py-24 md:py-32 px-6 text-center relative overflow-hidden"
        style={{ background: 'var(--color-bg)' }}
      >
        <div className={`max-w-3xl mx-auto reveal-fade-up ${hero.visible ? 'is-revealed' : ''}`}>
          <span className="section-eyebrow">For Coaching Centres</span>
          <h1 className="section-title mt-2">
            100% visibility into every student&rsquo;s learning.
          </h1>
          <p className="section-subtitle mx-auto mt-4" style={{ maxWidth: 540 }}>
            IntelGrader is the operating system for learning — turning every
            handwritten answer into a live position in your curriculum graph.
          </p>
          <div className="flex gap-3 justify-center flex-wrap mt-8">
            <a href="/book-demo" onClick={() => trackEvent('book_demo_click', 'CTA', 'coaching_hero')} className="btn btn-primary btn-lg">Book a Demo &rarr;</a>
            <a href="/#how-it-works" className="btn btn-secondary btn-lg">See How It Works</a>
          </div>
        </div>
      </section>

      {/* ——— THE PROBLEM (sticky notes) ——— */}
      <section ref={problem.ref} className="py-24 px-6">
        <div className={`max-w-[1250px] mx-auto reveal-fade-up ${problem.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">The Problem</span>
            <h2 className="section-title">Grading at scale produces scores. Not understanding.</h2>
            <p className="section-subtitle">
              Teachers check 30&ndash;50 papers per student per week. That&rsquo;s 10&ndash;20 hours
              of checking time &mdash; and zero insight into which concept each student keeps getting wrong.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            {PROBLEMS.map((p, i) => (
              <div
                key={p.cat}
                className="sticky-note"
                style={{
                  '--sticky-bg': '#dff0ff',
                  '--sticky-tape': 'rgba(61,123,217,0.20)',
                  '--sticky-icon-color': '#3d7bd9',
                  transform: `rotate(${p.rotation})`,
                  animationDelay: `${i * 150}ms`,
                } as React.CSSProperties}
              >
                <div className="sticky-note__tape" />
                <div className="sticky-note__cat" style={{ color: '#3d7bd9' }}>{p.cat}</div>
                <h3 className="sticky-note__title">{p.title}</h3>
                <p className="sticky-note__body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ——— WHAT CHANGES ——— */}
      <section
        ref={features.ref}
        className="market-page-band py-24 px-6"
        style={{ background: 'var(--color-bg)' }}
      >
        <div className={`max-w-[1250px] mx-auto reveal-fade-up ${features.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">What Changes</span>
            <h2 className="section-title">From grading overhead to learning intelligence.</h2>
          </div>

          <div className="mt-8">
            <StationeryCapabilityBoard panels={FEATURE_PANELS} variant="trio" />
          </div>
        </div>
      </section>

      {/* ——— VALUE PROP (sketch board comparison) ——— */}
      <section ref={comparison.ref} className="py-24 px-6">
        <div className={`max-w-[900px] mx-auto reveal-fade-up ${comparison.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">The Real Difference</span>
            <h2 className="section-title">Manual grading produces scores. IntelGrader produces learning data.</h2>
          </div>

          <div className="sketch-board mt-8">
            <div className="sketch-board__grid" />
            <div className="sketch-table" style={{ gridTemplateColumns: '1.5fr 1fr 1fr' }}>
              {/* Header */}
              <div className="sketch-table__row sketch-table__row--head" style={{ display: 'contents' }}>
                <div className="sketch-table__cell sketch-table__cell--label" />
                <div className="sketch-table__cell sketch-header-text">Manual</div>
                <div className="sketch-table__cell sketch-header-text sketch-header-text--intel">IntelGrader</div>
              </div>
              {/* Rows */}
              {COMP_ROWS.map((row) => (
                <div key={row.label} className="sketch-table__row" style={{ display: 'contents' }}>
                  <div className="sketch-table__cell sketch-table__cell--label sketch-label-text">{row.label}</div>
                  <div className="sketch-table__cell" style={{ fontFamily: 'var(--font-caveat)', fontSize: 18, color: '#4a5568' }}>{row.manual}</div>
                  <div className="sketch-table__cell sketch-table__cell--intel" style={{ fontFamily: 'var(--font-caveat)', fontSize: 18 }}>
                    <RoughAnnotation type="highlight" color="rgba(43,123,255,0.08)" padding={4} animationDuration={0}>
                      {row.intel}
                    </RoughAnnotation>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ——— FAQ ——— */}
      <section
        ref={faq.ref}
        className="py-24 px-6"
        style={{ background: 'var(--color-bg)' }}
      >
        <div className={`max-w-3xl mx-auto reveal-fade-up ${faq.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">FAQ</span>
            <h2 className="section-title">Common questions from coaching centres.</h2>
          </div>

          <div className="mt-8 space-y-4">
            {FAQS.map((item) => (
              <div key={item.q} className="glass-card p-6">
                <h3 className="text-base font-semibold text-[var(--color-text)] mb-2">{item.q}</h3>
                <p className="text-sm text-[var(--color-muted)] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ——— CTA ——— */}
      <section className="py-24 px-6">
        <div
          className="max-w-[1250px] mx-auto text-center py-20 px-8 relative overflow-hidden"
          style={{ background: 'linear-gradient(175deg, #428fe9, #3475db)', borderRadius: 24 }}
        >
          <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight">
            See it work with your curriculum.
          </h2>
          <p className="text-white/70 text-lg mt-3" style={{ fontFamily: 'var(--font-caveat)' }}>
            Upload a worksheet. Get concept-mapped feedback in seconds.
          </p>
          <div className="mt-8">
            <a href="/book-demo" onClick={() => trackEvent('book_demo_click', 'CTA', 'coaching_bottom')} className="btn btn-lg bg-white text-[#428fe9] hover:bg-white/90 font-bold">
              Book a Demo &rarr;
            </a>
          </div>
        </div>
      </section>

      {/* FAQ JSON-LD for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          }),
        }}
      />
    </div>
  );
};

export default CoachingCentersPage;
